import type { NightSortField } from '@/gql/graphql';
import { int, oneOf } from '@/hooks/useUrlState';

/** The two layouts of `/nights`; the calendar is the default, so a bare `/nights` is unchanged. */
export type NightsLayout = 'calendar' | 'grid';

const layout = oneOf<NightsLayout, NightsLayout>(
  { calendar: true, grid: true },
  'calendar',
);

/** Just the layout: `NightsPage` reads it to pick the calendar or the grid. */
export const nightsLayoutUrlSchema = { layout };

export type NightsGridSortColumn = 'title' | 'doorsOpen' | 'doorsClose';

/** DataGrid column `id` → GraphQL sort field for server-side sort; its keys are the values `sort` accepts. */
export const NIGHT_COLUMN_SORT: Record<NightsGridSortColumn, NightSortField> = {
  title: 'TITLE',
  doorsOpen: 'DOORS_OPEN',
  doorsClose: 'DOORS_CLOSE',
};

/** The next night first. */
export const NIGHTS_GRID_DEFAULT_SORT: {
  sort: NightsGridSortColumn;
  dir: 'asc' | 'desc';
} = { sort: 'doorsOpen', dir: 'asc' };

/**
 * The /nights grid query string, in this order (see README "List pages, URL state"). The two filters are ON by
 * default, so the URL carries them only when switched OFF: `ticketed=all` shows door-only nights too, `active=all`
 * shows inactive nights too.
 */
export const nightsGridUrlSchema = {
  layout,
  ticketed: oneOf<'all', ''>({ all: true }, ''),
  active: oneOf<'all', ''>({ all: true }, ''),
  sort: oneOf(NIGHT_COLUMN_SORT, NIGHTS_GRID_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>(
    { asc: true, desc: true },
    NIGHTS_GRID_DEFAULT_SORT.dir,
  ),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 25),
};
